import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, BadgeEuro, Sparkles, ArrowRight } from "lucide-react";
import { CATEGORY_CARDS } from "@/lib/data";
import { getDeals } from "@/lib/content";
import DealsTable from "@/components/DealsTable";
import Cta from "@/components/Cta";

const HERO_IMG =
  "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

const TRUST = [
  { icon: BadgeEuro, title: "Bespaar tijd en geld", text: "Vergelijk prijzen van alle grote aanbieders op een plek." },
  { icon: ShieldCheck, title: "Betrouwbaar", text: "Alleen samenwerken met gecertificeerde partners." },
  { icon: Zap, title: "Snel en eenvoudig", text: "In 3 stappen naar je ideale ticket." },
  { icon: Sparkles, title: "Gratis service", text: "Geen extra kosten, je betaalt dezelfde prijs als bij de aanbieder." },
];

export default function Home() {
  const deals = getDeals("popular");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Concertpubliek" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C3E50]/70" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-40 text-center text-white">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block text-xs sm:text-sm uppercase tracking-[0.2em] font-medium bg-white/15 backdrop-blur px-4 py-1.5 rounded-full mb-6"
          >
            Vergelijk &amp; Bespaar op Tickets
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter max-w-4xl mx-auto leading-tight"
          >
            Vind de Beste Ticket Deals op Tixter.nl
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 text-lg sm:text-xl text-slate-200 max-w-2xl mx-auto"
          >
            Vergelijk prijzen voor concerten, festivals, vliegtickets en meer. Bespaar tijd en geld!
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-9 flex flex-wrap justify-center gap-4"
          >
            <Link
              to="/concerttickets"
              data-testid="hero-cta-primary"
              className="bg-[#FF6B35] hover:bg-[#E55A2A] text-white font-semibold rounded-full px-8 py-4 transition-all hover:-translate-y-1 shadow-lg inline-flex items-center gap-2"
            >
              Bekijk Deals Nu <ArrowRight size={18} />
            </Link>
            <Link
              to="/blog"
              className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white font-semibold rounded-full px-8 py-4 transition-all"
            >
              Lees onze tips
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.2em] font-medium text-[#FF6B35]">Categorieen</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#2C3E50] mt-2">
            Waar wil jij naartoe?
          </h2>
        </div>
        <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORY_CARDS.map((c, i) => (
            <motion.div
              key={c.to}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <Link
                to={c.to}
                data-testid={`category-card-${c.to.replace("/", "")}`}
                className="group block rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-3 left-3 text-2xl">{c.emoji}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-[#2C3E50]">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B35]">
                    {c.cta} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* POPULAR DEALS */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-[#2C3E50] mb-10">
          🔥 Populaire Deals Deze Week
        </h2>
        <DealsTable
          columns={["Evenement", "Aanbieder", "Prijs", "Beschikbaarheid", ""]}
          rows={deals}
          fields={["event", "provider", "price", "availability"]}
        />
      </section>

      {/* TRUST */}
      <section className="bg-[#2C3E50]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Waarom Tixter.nl?
          </h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map((t) => (
              <div key={t.title} className="text-center">
                <div className="mx-auto mb-4 grid place-items-center w-14 h-14 rounded-2xl bg-[#FF6B35]/15 text-[#FF6B35]">
                  <t.icon size={26} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-white">{t.title}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Cta />
    </div>
  );
}
